import { useRef } from 'react';
import * as Y from 'yjs';
import { NodeResizer, useConnection, useInternalNode, useStore } from '@xyflow/react';
import styles from './selection-transformer.module.css';
import { SelectionToolbar } from './surfaces/floating/selection-toolbar';
import {
  SelectionHandles,
  type ExpandDirection,
} from './surfaces/handles/selection-handles';
import { useNodeActions } from '@/features/canvas/engine/wiring/contexts/node-actions-context';
import { useFormattingState } from '@/features/canvas/engine/hooks/use-formatting-state';
import { useYjsLocked } from '@/features/canvas/engine/hooks/use-yjs-locked';
import { useFrameData } from '@/features/canvas/engine/hooks/use-frame-data';
import { useConnectorMode } from '@/features/canvas/engine/wiring/contexts/connector-mode-context';
import type { NodeType } from '@/features/canvas/utils/node-types';
import type { FreedrawState } from './surfaces/floating/node-formatting-toolbar';
import type { GifState } from './surfaces/floating/node-floating-toolbar';
import { NODE_MIN_SIZE, FRAME_COLLAPSED_SIZE } from '@/features/canvas/utils/constants';

// Re-export ExpandDirection at the orchestrator's path for callers that
// already import it from here. SelectionHandles is the canonical home
// for the type now.
export type { ExpandDirection };

// Frames are included so FrameFloatingToolbar's FrameFormattingToolbar
// receives a populated FormattingState — the title bar formats with the
// same font/size/style/align/color controls as Sticky/Shape/Text.
const FORMATTING_TYPES = new Set<NodeType>(['sticky', 'shape', 'text', 'frame']);

export interface SelectionTransformerProps {
  nodeId: string;
  nodeType: NodeType;
  selected: boolean;
  dragging?: boolean;
  locked?: boolean;
  editing?: boolean;
  /** Set false to suppress NodeResizer entirely (e.g. auto-sizing text nodes). Defaults true. */
  resizable?: boolean;
  /** Omit to hide expand dots entirely. */
  onExpand?: (nodeId: string, direction: ExpandDirection) => void;
  /** Content rendered inside the expand ghost preview. Renders a plain box if omitted. */
  ghostContent?: React.ReactNode;
  /** Yjs doc — when provided, formatting controls are included in the context toolbar. */
  doc?: Y.Doc;
  /** Freedraw stroke state — when provided, stroke color + thickness controls are shown. */
  freedrawState?: FreedrawState;
  /** GIF play/pause state — when provided, a play/pause button is shown in the toolbar. */
  gifState?: GifState;
  children: React.ReactNode;
  className?: string;
}

/**
 * Wraps any canvas node with selection chrome:
 *  - RF NodeResizer (8-handle resize)
 *  - Single NodeToolbar above the node: formatting controls + context actions
 *  - 4 directional expand dots with ghost preview on hover
 */
export function SelectionTransformer({
  nodeId,
  nodeType,
  selected,
  dragging = false,
  locked = false,
  editing = false,
  resizable = true,
  onExpand,
  ghostContent,
  doc,
  freedrawState,
  gifState,
  children,
  className,
}: SelectionTransformerProps) {
  const actions = useNodeActions();
  const connectorMode = useConnectorMode();

  const internalNode = useInternalNode(nodeId);
  const nodeW = internalNode?.measured?.width ?? 160;
  const nodeH = internalNode?.measured?.height ?? 120;

  // RF caches per-handle bounding boxes (handleBounds) from the node DOM
  // ONCE per measurement. The visible expand-dot <Handle>s only mount
  // when a node is selected (via showHandles below) — but they're not in
  // the cache until a remeasure happens. Without that, drag-from-handle
  // silently fails on a freshly-selected node. Force a remeasure when:
  //   1. the node first mounts (initial cache build), AND
  //   2. showHandles flips (visible source handles appeared/disappeared).
  // This matches what NodeResizer triggers via dimension change, which is
  // why drag started working after you resized a node.

  const yjsLocked = useYjsLocked(doc, nodeId);
  // Frame-specific live data (only consumed when nodeType === 'frame'). The
  // hook still runs unconditionally to keep React's rules-of-hooks invariant,
  // but it cheaply no-ops when there's no doc or no frame node.
  const frameData = useFrameData(doc, nodeId);
  // Yjs doc is authoritative when present; fall back to prop for node types
  // that don't pass doc (e.g. frame without formatting).
  const isLocked = doc ? yjsLocked : locked;

  const selectedCount = useStore((s) => s.nodes.filter((n) => n.selected).length);
  const zoom = useStore((s) => s.transform[2]);
  // True while any user is mid-connection-drag. We hide the expand ghost in
  // this state so the connector preview line is the only visual feedback.
  const isConnecting = useConnection((c) => c.inProgress);
  // True when any edge on the canvas is selected — we suppress all node
  // hover chrome (drop handles, expand dots, ghost) so the user can drag
  // the edge's reconnect anchors without nodes intercepting pointer events.
  // Once a reconnect drag begins (isConnecting flips true), we re-enable
  // the chrome so the user has drop targets to land on.
  const anyEdgeSelected = useStore((s) => {
    let any = false;
    s.edges.forEach((e) => { if (e.selected) any = true; });
    return any;
  });
  const suppressForEdgeSelection = anyEdgeSelected && !isConnecting;
  const solo = selectedCount === 1;
  const showResizer = resizable && selected && solo && !isLocked && !editing;
  const showHandles = selected && solo && !isLocked && !suppressForEdgeSelection;
  const showToolbar = selected && solo && !dragging;
  const showFormatting = showToolbar && !!doc && FORMATTING_TYPES.has(nodeType);

  // Stable fallback doc so useFormattingState never receives a new Y.Doc()
  // on every render when the real doc prop is absent.
  const fallbackDocRef = useRef<Y.Doc | null>(null);
  if (!fallbackDocRef.current) fallbackDocRef.current = new Y.Doc();
  const effectiveDoc = doc ?? fallbackDocRef.current;
  const formattingState = useFormattingState(effectiveDoc, nodeId) ?? undefined;

  const minSize = NODE_MIN_SIZE[nodeType as keyof typeof NODE_MIN_SIZE];

  // Counter-zoom factor for the expand dots so they keep a constant visual
  // size on screen regardless of the canvas zoom level. RF applies the zoom
  // transform to the entire viewport (and everything inside it, including
  // these dots); the inverse-zoom CSS var below cancels it out for the dot.
  const inverseZoom = zoom > 0 ? 1 / zoom : 1;

  return (
    <div
      className={className ?? styles.root}
      style={{
        ...(minSize ? { '--node-min-w': `${minSize.width}px`, '--node-min-h': `${minSize.height}px` } : {}),
        '--inverse-zoom': inverseZoom,
      } as React.CSSProperties}
    >
      {actions && (
        <SelectionToolbar
          nodeId={nodeId}
          nodeType={nodeType}
          isVisible={showToolbar}
          locked={isLocked}
          showFormatting={showFormatting}
          frameData={frameData}
          doc={doc}
          formattingState={formattingState}
          freedrawState={freedrawState}
          gifState={gifState}
          actions={actions}
        />
      )}

      <NodeResizer
        isVisible={showResizer}
        // Collapsed frames floor to a SQUARE min — the folder reads
        // best as a square (icon + title centered), and an asymmetric
        // floor would let the user produce stretched-rectangle folders
        // that look broken. Open frames use the rectangular open-state
        // min from NODE_MIN_SIZE (120×80 for frames).
        minWidth={
          nodeType === 'frame' && frameData.collapsed
            ? FRAME_COLLAPSED_SIZE.width
            : NODE_MIN_SIZE[nodeType as keyof typeof NODE_MIN_SIZE]?.width ?? 40
        }
        minHeight={
          nodeType === 'frame' && frameData.collapsed
            ? FRAME_COLLAPSED_SIZE.height
            : NODE_MIN_SIZE[nodeType as keyof typeof NODE_MIN_SIZE]?.height ?? 40
        }
      />

      {/* Per-side handles + expand dots + ghost preview. Frames are
          excluded — they're containers, not connection sources, and
          the expand "+" affordance makes no sense on a container.
          Locked + suppressForEdgeSelection are guarded inside the
          component too, but excluding here avoids the mount cost
          entirely on frames. */}
      {nodeType !== 'frame' && (
        <SelectionHandles
          nodeId={nodeId}
          nodeType={nodeType}
          nodeWidth={nodeW}
          nodeHeight={nodeH}
          dragging={dragging}
          locked={isLocked}
          suppressForEdgeSelection={suppressForEdgeSelection}
          isConnecting={isConnecting}
          connectorMode={connectorMode}
          showHandles={showHandles}
          onExpand={onExpand}
          ghostContent={ghostContent}
        />
      )}

      {children}
    </div>
  );
}
